// 261010 P2-1 (luồng Sky) — logic THUẦN của panel "Sơ đồ định vị": dựng ma trận gán, nhãn trạng thái, diff liên kết, lọc lỗi, khoảng giá…
// KHÔNG import antd/React: dưới jest của react-scripts antd v6 + react-router v7 không load được (xem CLAUDE.md) ⇒ logic tách khỏi component
// (mẫu `skyVideoMetaUtils.ts`, `promptChipUtils.ts`).
import type {
  ApprovalInfo, FilmFailedJob, FilmLocationZone, FilmRunningJob, PositionMap, PositionMapCast, PositionMapDriftReason, PositionMapDrifted, PositionMapLink,
  PositionMapRole, PositionMapStage,
} from '../../types/film';
import { apiErrorText } from './apiErrorText';

export const POSITION_JOB = { MAP: 'FILM_POSITION_MAP', SETUPS: 'FILM_POSITION_SETUPS' } as const;
const POSITION_JOB_TYPES: string[] = [POSITION_JOB.MAP, POSITION_JOB.SETUPS];
export const isPositionJobType = (type: string): boolean => POSITION_JOB_TYPES.includes(type);

// ───────────────────────── Ma trận gán (video → giai đoạn → setup) ─────────────────────────

/** Phần của plan mà ma trận cần: không import kiểu FilmEvent đầy đủ để test dựng fixture nhỏ. */
export interface MatrixScene {
  id: string;
  orderIndex: number;
  events: Array<{ id: string; eventOrder: number; videoMeta?: { phases?: Array<{ order: number; purpose?: string }> | null } | null }>;
}

export interface MatrixRow {
  key: string;
  eventId: string;
  /** NULL = cả video — CHỈ cho event không có giai đoạn (BE từ chối NULL trên event có giai đoạn). */
  phaseOrder: number | null;
  sceneLabel: string;
  eventLabel: string;
  stageLabel: string;
  /** Setup đang gán (liên kết HỢP LỆ, đã lưu ở server). */
  mapId: string | null;
  /** Liên kết cũ của hàng này nhưng đã lệch (vd nội dung giai đoạn đổi sau khi gán). Không tính vào `mapId`. */
  drift: { reason: PositionMapDriftReason; mapId: string } | null;
  /** Server báo giai đoạn này chưa có setup (`uncoveredStages`). */
  uncovered: boolean;
}

export const stageKey = (eventId: string, phaseOrder: number | null): string => `${eventId}:${phaseOrder === null ? 'null' : phaseOrder}`;

export function buildMatrix(
  scenes: MatrixScene[],
  valid: PositionMapLink[],
  drifted: PositionMapDrifted[],
  uncovered: PositionMapStage[],
): MatrixRow[] {
  const validBy = new Map(valid.map((l) => [stageKey(l.eventId, l.phaseOrder), l.positionMapId]));
  const driftBy = new Map(drifted.map((d) => [stageKey(d.link.eventId, d.link.phaseOrder), d]));
  const uncoveredKeys = new Set(uncovered.map((s) => stageKey(s.eventId, s.phaseOrder)));
  const rows: MatrixRow[] = [];
  for (const scene of [...scenes].sort((a, b) => a.orderIndex - b.orderIndex)) {
    for (const event of [...scene.events].sort((a, b) => a.eventOrder - b.eventOrder)) {
      const phases = event.videoMeta?.phases;
      const stages: Array<{ phaseOrder: number | null; label: string }> = Array.isArray(phases) && phases.length
        ? [...phases].sort((a, b) => a.order - b.order).map((p) => ({ phaseOrder: p.order, label: `GĐ${p.order}${p.purpose ? ` · ${p.purpose}` : ''}` }))
        : [{ phaseOrder: null, label: 'Cả video' }];
      for (const stage of stages) {
        const key = stageKey(event.id, stage.phaseOrder);
        const mapId = validBy.get(key) ?? null;
        const d = !mapId ? driftBy.get(key) : undefined;
        rows.push({
          key, eventId: event.id, phaseOrder: stage.phaseOrder, sceneLabel: `S${scene.orderIndex}`, eventLabel: `E${event.eventOrder}`, stageLabel: stage.label,
          mapId, drift: d ? { reason: d.reason, mapId: d.link.positionMapId } : null, uncovered: uncoveredKeys.has(key),
        });
      }
    }
  }
  return rows;
}

/** Liên kết lệch có hàng trong ma trận (đổi nội dung giai đoạn…) vs mồ côi (event/giai đoạn đã mất, cả-video trên event có giai đoạn). */
export function splitDrifted(rows: MatrixRow[], drifted: PositionMapDrifted[]): { onRows: PositionMapDrifted[]; orphans: PositionMapDrifted[] } {
  const keys = new Set(rows.map((r) => r.key));
  const onRows: PositionMapDrifted[] = [];
  const orphans: PositionMapDrifted[] = [];
  for (const d of drifted) (keys.has(stageKey(d.link.eventId, d.link.phaseOrder)) ? onRows : orphans).push(d);
  return { onRows, orphans };
}

export type MatrixDraft = Record<string, string | null>;

/** Bản nháp khởi tạo = liên kết hợp lệ đã lưu. Hàng lệch để TRỐNG: chỉ người bấm "Giữ"/chọn lại mới chấp nhận nội dung mới. */
export const initDraft = (rows: MatrixRow[]): MatrixDraft => Object.fromEntries(rows.map((r) => [r.key, r.mapId]));

/** Bỏ khỏi nháp những setup đã bị xoá (nếu không PUT sẽ 400 BAD_LINKS/MAP_GONE). */
export const sanitizeDraft = (draft: MatrixDraft, mapIds: Set<string>): MatrixDraft =>
  Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v && mapIds.has(v) ? v : null]));

/** Body `links[]` của PUT: chỉ hàng có setup. KHÔNG gửi liên kết lệch (BE sẽ 400 hoặc âm thầm chấp nhận nội dung đã đổi). */
export const linksPayload = (rows: MatrixRow[], draft: MatrixDraft): Array<{ eventId: string; phaseOrder: number | null; positionMapId: string }> =>
  rows.filter((r) => !!draft[r.key]).map((r) => ({ eventId: r.eventId, phaseOrder: r.phaseOrder, positionMapId: draft[r.key] as string }));

/** `changed` = ô khác bản đã lưu · `dropped` = liên kết lệch sẽ BỊ BỎ khi lưu (hàng lệch chưa chọn lại + mồ côi). */
export function countPendingChanges(rows: MatrixRow[], draft: MatrixDraft, orphans: PositionMapDrifted[]): { changed: number; dropped: number } {
  let changed = 0;
  let dropped = orphans.length;
  for (const r of rows) {
    if ((draft[r.key] ?? null) !== (r.mapId ?? null)) changed += 1;
    if (r.drift && !draft[r.key]) dropped += 1;
  }
  return { changed, dropped };
}

/** Đổi khi tập hàng đổi (event bị tách/gộp, thêm giai đoạn…) — dùng quyết định có reset bản nháp hay không. */
export const signatureOfRows = (rows: MatrixRow[]): string => rows.map((r) => r.key).join('|');

/** "Setup này đang dùng cho video/giai đoạn nào" — theo liên kết đã lưu. VD `['E1 · GĐ1,2', 'E3 · cả video']`. */
export function usageLabels(mapId: string, rows: MatrixRow[]): string[] {
  const byEvent = new Map<string, { label: string; phases: Array<number | null> }>();
  for (const r of rows) {
    if (r.mapId !== mapId) continue;
    const g = byEvent.get(r.eventId) || { label: r.eventLabel, phases: [] };
    g.phases.push(r.phaseOrder);
    byEvent.set(r.eventId, g);
  }
  return Array.from(byEvent.values()).map((g) => `${g.label} · ${g.phases.every((p) => p === null) ? 'cả video' : `GĐ${g.phases.filter((p) => p !== null).join(',')}`}`);
}

const DRIFT_TEXT: Record<PositionMapDriftReason, string> = {
  EVENT_GONE: 'video không còn trong plan',
  PHASE_GONE: 'giai đoạn không còn trong video',
  WHOLE_VIDEO_NOT_ALLOWED: 'video đã có giai đoạn nên không gán cả video được',
  STAGE_CHANGED: 'nội dung giai đoạn đã đổi sau khi gán — xác nhận lại setup này còn đúng',
  MAP_GONE: 'setup không còn',
};
export const driftReasonText = (reason: PositionMapDriftReason | string): string => DRIFT_TEXT[reason as PositionMapDriftReason] || String(reason);

// ───────────────────────── Thẻ setup: nhãn trạng thái, khoảng giá, điều kiện gen ─────────────────────────

export interface StatusTag { key: string; text: string; color: string; tip?: string }

/**
 * Nhãn trạng thái của một thẻ setup, thứ tự: đang gen → lỗi → (chưa có ảnh | cũ + lý do) → prompt sửa tay.
 * "Cũ" do BE tính lúc đọc (`staleness`); ở đây chỉ dịch ra lý do tiếng Việt.
 */
export function mapStatusTags(map: Pick<PositionMap, 'staleness' | 'promptOverride'>, st: { running?: boolean; error?: string | null }): StatusTag[] {
  const tags: StatusTag[] = [];
  if (st.running) tags.push({ key: 'running', text: 'đang gen', color: 'processing', tip: 'Đang gen ảnh ở nền — đừng bấm lại, mỗi lượt là một lần tính tiền.' });
  if (st.error) tags.push({ key: 'error', text: 'lỗi', color: 'error', tip: st.error });
  if (map.staleness === 'NO_IMAGE') tags.push({ key: 'noimg', text: 'chưa có ảnh', color: 'default' });
  else if (map.staleness === 'INPUT_CHANGED') tags.push({ key: 'stale', text: 'cũ · đầu vào đã đổi', color: 'warning', tip: 'Từ lúc gen ảnh này, đầu vào đã đổi: chữ dàn cảnh, zone, look của nhân vật, ảnh sheet/bối cảnh, prompt sửa tay hoặc model. Gen lại để ảnh khớp.' });
  else if (map.staleness === 'ANCHOR_REGENERATED') tags.push({ key: 'stale', text: 'cũ · ảnh chính đã gen lại', color: 'warning', tip: 'Ảnh chính của bối cảnh này đã được gen lại sau ảnh phụ này — ảnh phụ không còn bám đúng khung của ảnh chính. Gen lại ảnh phụ.' });
  if (map.promptOverride != null && map.promptOverride !== '') tags.push({ key: 'override', text: 'prompt sửa tay', color: 'purple', tip: 'Đang dùng prompt do người sửa, không theo mẫu. Bấm "Về mặc định" để quay lại.' });
  return tags;
}

// ───────────────────────── 261010 P4-1 — nút Duyệt (dùng chung sơ đồ + storyboard) ─────────────────────────

export interface ApprovalView {
  /** Nhãn trạng thái (xanh đã duyệt · cam đã duyệt nhưng đã đổi · xám chưa duyệt). */
  tag: StatusTag;
  /** Nút "Duyệt"/"Duyệt lại"; `null` khi đã duyệt và còn hiệu lực. `ok:false` + `reason` = khoá (BE sẽ trả 409 NO_IMAGE / STALE). */
  approve: { label: string; ok: boolean; reason?: string } | null;
  /** Có bản duyệt để gỡ (đã duyệt, kể cả bản đã đổi). */
  canUnapprove: boolean;
}

const APPROVE_TIP = 'Chỉ bản đã duyệt mới vào video.';

/**
 * `approval` do BE tính lúc đọc (so băm ảnh + đầu vào) — FE chỉ dịch. Thiếu trường (BE cũ) ⇒ coi là chưa duyệt.
 * `hasImage`/`stale` chỉ để khoá nút Duyệt sớm cho người dùng; cổng thật vẫn là 409 của BE.
 */
export function approvalView(approval: ApprovalInfo | null | undefined, st: { hasImage: boolean; stale: boolean }): ApprovalView {
  const state = approval?.state || 'NONE';
  const by = approval?.approvedBy ? ` bởi ${approval.approvedBy}` : '';
  const when = approval?.approvedAt ? ` lúc ${new Date(approval.approvedAt).toLocaleString('vi-VN')}` : '';
  const gate = (label: string): NonNullable<ApprovalView['approve']> => {
    if (!st.hasImage) return { label, ok: false, reason: 'Chưa có ảnh để duyệt — gen ảnh trước.' };
    if (st.stale) return { label, ok: false, reason: 'Ảnh đã cũ so với đầu vào — gen lại ảnh rồi mới duyệt.' };
    return { label, ok: true };
  };
  if (state === 'APPROVED') {
    return { tag: { key: 'approval', text: 'đã duyệt', color: 'success', tip: `Đã duyệt${by}${when}. ${APPROVE_TIP} Sửa hoặc gen lại ảnh sẽ làm duyệt mất hiệu lực.` }, approve: null, canUnapprove: true };
  }
  if (state === 'OUTDATED') {
    return {
      tag: { key: 'approval', text: 'đã duyệt nhưng đã đổi — duyệt lại', color: 'warning', tip: `Đã duyệt${by}${when} nhưng ảnh hoặc đầu vào đã đổi từ đó nên KHÔNG còn vào video. Xem lại rồi duyệt lại (hoặc bỏ duyệt).` },
      approve: gate('Duyệt lại'), canUnapprove: true,
    };
  }
  return { tag: { key: 'approval', text: 'chưa duyệt', color: 'default', tip: `Chưa duyệt — ${APPROVE_TIP[0].toLowerCase()}${APPROVE_TIP.slice(1)}` }, approve: gate('Duyệt'), canUnapprove: false };
}

/** Sơ đồ: có ảnh = `imageUrl`; cũ = đầu vào đã đổi hoặc ảnh chính gen lại sau. */
export const mapApprovalView = (map: Pick<PositionMap, 'imageUrl' | 'staleness' | 'approval'>): ApprovalView =>
  approvalView(map.approval, { hasImage: !!map.imageUrl, stale: map.staleness === 'INPUT_CHANGED' || map.staleness === 'ANCHOR_REGENERATED' });

const APPROVAL_CODE_TEXT: Record<string, string> = {
  NO_IMAGE: 'chưa có ảnh — gen ảnh trước khi duyệt.',
  STALE: 'ảnh đã cũ so với đầu vào — gen lại ảnh rồi mới duyệt (đã nạp lại).',
};

/** Câu lỗi nút Duyệt: NO_IMAGE/STALE có câu riêng, mã khác (NOT_SKY_PLAN, bận…) rơi về hàm chung của khối (`fallback`). */
export function approvalErrorText(err: any, what: string, fallback: (e: any, w: string) => string): string {
  const code = err?.response?.data?.code;
  const base = typeof code === 'string' ? APPROVAL_CODE_TEXT[code] : undefined;
  return base ? `${what}: ${base}` : fallback(err, what);
}

const fmtUsd = (n: number): string => n.toFixed(2).replace('.', ',');
const ANCHOR_COST_HIGH = 0.27; // đo smoke 21/10: ảnh chính cùng 4 ref lần 1 $0,099, gen lại $0,265 ⇒ giá dao động, hiện khoảng
const ANCHOR_COST_LOW = 0.1;
const DERIVATIVE_COST = 0.28;

/** Nhãn giá ước hiện cạnh nút Gen. BE luôn trả phẳng 0.10/0.28 ⇒ khoảng của ảnh chính dựng ở FE. */
export function costRangeLabel(role: PositionMapRole, estimatedCostUsd?: number | null): string {
  if (role === 'DERIVATIVE') return `~$${fmtUsd(estimatedCostUsd && estimatedCostUsd > 0 ? estimatedCostUsd : DERIVATIVE_COST)}`;
  const low = estimatedCostUsd && estimatedCostUsd > 0 ? estimatedCostUsd : ANCHOR_COST_LOW;
  return `~$${fmtUsd(low)}–${fmtUsd(Math.max(ANCHOR_COST_HIGH, low))}`;
}

/** Giá THẬT của lượt gen gần nhất. `imageCostUsd` là Prisma Decimal ⇒ chuỗi. */
export function actualCostLabel(imageCostUsd: string | number | null | undefined): string | null {
  if (imageCostUsd === null || imageCostUsd === undefined || imageCostUsd === '') return null;
  const n = Number(imageCostUsd);
  return Number.isFinite(n) ? `đã tốn $${fmtUsd(n)}` : null;
}

/** Nút Gen khoá khi nào + vì sao (hiện ở tooltip). */
export function canGenerate(map: Pick<PositionMap, 'role' | 'locationId' | 'anchorMapId'>, allMaps: Array<Pick<PositionMap, 'id' | 'imageUrl'>>, st: { running?: boolean }): { ok: boolean; reason?: string } {
  if (st.running) return { ok: false, reason: 'Đang gen ở nền.' };
  if (!map.locationId) return { ok: false, reason: 'Setup chưa gắn bối cảnh nên không gen được.' };
  if (map.role === 'DERIVATIVE') {
    const anchor = allMaps.find((m) => m.id === map.anchorMapId);
    if (!anchor?.imageUrl) return { ok: false, reason: 'Cần gen ảnh chính của bối cảnh này trước.' };
  }
  return { ok: true };
}

/** Giữ khi chạy lại "AI đề xuất setup": đã có người/agent sửa (EDITED/AGENT) hoặc đã có ảnh. Bản sao của `isKeptOnPropose` ở BE (`filmPositionMap.ts`) — sửa một bên thì sửa cả hai. */
export function isKeptOnPropose(m: Pick<PositionMap, 'authorStatus' | 'imageUrl'>): boolean {
  return m.authorStatus === 'EDITED' || m.authorStatus === 'AGENT' || !!m.imageUrl;
}

/** Thay (AI, chưa có ảnh) vs giữ (đã sửa tay/agent sửa hoặc đã có ảnh) khi chạy lại "AI đề xuất setup" — khớp `planProposalWrite` ở BE. */
export function proposeImpact(maps: Array<Pick<PositionMap, 'name' | 'authorStatus' | 'imageUrl'>>): { replaced: string[]; kept: string[] } {
  const replaced: string[] = [];
  const kept: string[] = [];
  for (const m of maps) (isKeptOnPropose(m) ? kept : replaced).push(m.name);
  return { replaced, kept };
}

/** Nhãn nguồn của setup cạnh tên thẻ: người sửa ⇒ "đã sửa", agent sửa ⇒ "agent sửa" (đúng MỘT nhãn), AI viết ⇒ không nhãn. */
export function authorBadge(authorStatus: PositionMap['authorStatus']): { text: string; tip: string } | null {
  if (authorStatus === 'EDITED') return { text: 'đã sửa', tip: 'Đã có người sửa — chạy lại AI đề xuất sẽ giữ nguyên setup này.' };
  if (authorStatus === 'AGENT') return { text: 'agent sửa', tip: 'Agent (AI ngoài) đã tạo/sửa setup này — chạy lại AI đề xuất sẽ giữ nguyên. Người sửa tiếp thì nhãn đổi thành "đã sửa".' };
  return null;
}

export interface MapGroup { key: string; name: string; maps: PositionMap[] }
const NO_LOCATION_NAME = '(chưa gắn bối cảnh)';
const GONE_LOCATION_NAME = '(bối cảnh không còn trong Bank)';

/** Nhóm setup theo bối cảnh (thứ tự Bank); trong nhóm ảnh chính trước rồi theo `sortOrder`. */
export function groupMapsByLocation(maps: PositionMap[], locations: Array<{ id: string; name: string }>): MapGroup[] {
  const sorted = (list: PositionMap[]) => [...list].sort((a, b) => (a.role === b.role ? 0 : a.role === 'ANCHOR' ? -1 : 1) || a.sortOrder - b.sortOrder);
  const groups: MapGroup[] = [];
  const used = new Set<string>();
  for (const loc of locations) {
    const list = maps.filter((m) => m.locationId === loc.id);
    if (list.length) { groups.push({ key: loc.id, name: loc.name, maps: sorted(list) }); used.add(loc.id); }
  }
  const stray = maps.filter((m) => m.locationId && !used.has(m.locationId));
  if (stray.length) groups.push({ key: 'gone', name: GONE_LOCATION_NAME, maps: sorted(stray) });
  const none = maps.filter((m) => !m.locationId);
  if (none.length) groups.push({ key: 'none', name: NO_LOCATION_NAME, maps: sorted(none) });
  return groups;
}

// ───────────────────────── Cast + look ─────────────────────────

export type CastChange =
  | { type: 'look'; characterId: string; lookId: string | null }
  | { type: 'add'; characterId: string }
  | { type: 'remove'; characterId: string };

/**
 * Body `castJson` của PATCH.
 *
 * 🔴 Bẫy: GET trả `lookId` ĐÃ GIẢI cho mọi người (kể cả `lookSource` SCENE/MAIN) còn BE `validateCast` đặt `lookSource:'USER'` cho mọi
 * `lookId` khác null ⇒ gửi nguyên mảng từ GET ngược lại sẽ khoá cả đội vào look hiện tại, hết theo cảnh/bộ chính. Vì vậy chỉ người đã USER và
 * người vừa đổi mang `lookId`; số còn lại gửi `lookId:null` + nguồn cũ để BE tự giải lại.
 */
export function castPayload(cast: PositionMapCast[], change: CastChange): PositionMapCast[] {
  const keep = (c: PositionMapCast): PositionMapCast => (c.lookSource === 'USER' && c.lookId
    ? { characterId: c.characterId, lookId: c.lookId, lookSource: 'USER' }
    : { characterId: c.characterId, lookId: null, lookSource: c.lookSource === 'SCENE' ? 'SCENE' : 'MAIN' });
  if (change.type === 'remove') return cast.filter((c) => c.characterId !== change.characterId).map(keep);
  if (change.type === 'add') {
    const out = cast.map(keep);
    return out.some((c) => c.characterId === change.characterId) ? out : [...out, { characterId: change.characterId, lookId: null, lookSource: 'MAIN' }];
  }
  return cast.map((c) => (c.characterId === change.characterId
    ? { characterId: c.characterId, lookId: change.lookId, lookSource: change.lookId ? 'USER' : 'MAIN' } as PositionMapCast
    : keep(c)));
}

// ───────────────────────── Việc nền: theo dõi + lọc lỗi ─────────────────────────

export const runningPositionJobs = (jobs: FilmRunningJob[]): FilmRunningJob[] => jobs.filter((j) => isPositionJobType(j.type));

/** Job sơ đồ có ở lượt poll TRƯỚC mà mất ở lượt NÀY ⇒ vừa xong (thành công hoặc lỗi) ⇒ nạp lại danh sách. */
export function finishedPositionJobs(prev: FilmRunningJob[] | null, next: FilmRunningJob[]): FilmRunningJob[] {
  if (!prev) return [];
  const nextIds = new Set(next.map((j) => j.id));
  return prev.filter((j) => !nextIds.has(j.id));
}

/**
 * `PUT links` chạy dưới khoá `FILM_POSITION_SETUPS` nên lỗi kiểm tra của nó (400 BAD_LINKS / 409 LINKS_CHANGED) cũng được ghi thành job FAILED
 * và lọt vào `failures` của plan — người dùng đã nhận lỗi ngay trên response, băng đỏ đó chỉ là nhiễu. Chỉ lọc ĐÚNG hai câu này ở ĐÚNG loại việc này;
 * "Dữ liệu đổi trong lúc AI chạy, chạy lại" là lỗi thật của propose ⇒ giữ.
 */
export const PUT_LINKS_NOISE_NOTES = ['Liên kết không hợp lệ', 'Liên kết đã đổi, hãy nạp lại'];
export const isPutLinksNoise = (f: Pick<FilmFailedJob, 'type' | 'note'>): boolean =>
  f.type === POSITION_JOB.SETUPS && PUT_LINKS_NOISE_NOTES.includes((f.note || '').trim());
export const filterVisibleFailures = <T extends Pick<FilmFailedJob, 'type' | 'note'>>(failures: T[]): T[] => failures.filter((f) => !isPutLinksNoise(f));

/**
 * Lỗi nào thuộc setup nào? `failures[]` KHÔNG mang `key` (mapId) ⇒ nối bằng `jobId` mà 202 trả về lúc bấm Gen (= id việc = `failures[].id`),
 * lưu trong phiên. Sang phiên khác/F5 thì mất liên kết này — lỗi vẫn hiện ở băng cấp plan.
 */
export function failureForMap<T extends Pick<FilmFailedJob, 'id'>>(mapId: string, sessionJobs: Record<string, string>, failures: T[]): T | null {
  return failures.find((f) => sessionJobs[f.id] === mapId) || null;
}

// ───────────────────────── Lỗi tiếng Việt ─────────────────────────

const CODE_TEXT: Record<string, string> = {
  NOT_SKY_PLAN: 'sơ đồ định vị chỉ dùng cho plan dùng luồng mới.',
  NO_PHASES: 'plan chưa có giai đoạn video nên chưa đề xuất được setup.',
  NO_LOCATION: 'chưa có bối cảnh khớp trong Location Bank (hoặc setup chưa gắn bối cảnh) — thêm/lấy bối cảnh trước.',
  NO_PLATE: 'bối cảnh chưa có ảnh plate — gen ảnh bối cảnh trước.',
  ANCHOR_NOT_READY: 'ảnh chính của bối cảnh này chưa có — gen ảnh chính trước.',
  ANCHOR_RULE: 'mỗi bối cảnh chỉ có một setup chính; setup phụ phải trỏ tới setup chính cùng bối cảnh.',
  HAS_DERIVATIVES: 'setup chính này còn setup phụ trỏ vào — xoá hoặc đổi các setup phụ trước.',
  LINKS_CHANGED: 'liên kết đã đổi ở nơi khác (hoặc AI vừa ghi) — đã nạp lại, hãy xem lại rồi làm lại.',
  BAD_LINKS: 'liên kết hoặc dữ liệu setup không hợp lệ.',
  BAD_LOOK: 'nhân vật hoặc look không thuộc truyện/nhân vật này.',
  NOT_FOUND: 'không tìm thấy (có thể đã bị xoá).',
};

/** Câu lỗi tiếng Việt cho envelope `{code, message, details?}` của route sơ đồ; không nhận ra mã ⇒ rơi về `apiErrorText` chung (kể cả 409 bận). */
export function positionMapErrorText(err: any, what: string): string {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  const base = CODE_TEXT[code];
  if (!base) return apiErrorText(err, what);
  let extra = '';
  if (code === 'BAD_LINKS' && Array.isArray(d.details) && d.details.length) {
    const reasons = Array.from(new Set(d.details.map((x: any) => driftReasonText(x?.reason)).filter(Boolean)));
    extra = ` (${reasons.join('; ')})`;
  }
  return `${what}: ${base}${extra}`;
}

/** `warnings[]` của GET là mã thô (không gắn với setup nào) ⇒ dịch ra câu người đọc được; mã lạ giữ nguyên. */
export function warningText(w: string): string {
  if (w.startsWith('SHEET_MISSING:')) return `Nhân vật "${w.slice('SHEET_MISSING:'.length)}" chưa có ảnh sheet nên bị bỏ khỏi ảnh tham chiếu (vẫn có trong chữ dàn cảnh).`;
  if (w === 'REFS_CAPPED') return 'Có setup quá nhiều nhân vật: ảnh tham chiếu bị cắt ở 8 ảnh.';
  if (w === 'ZONE_MISSING') return 'Có setup trỏ tới zone không còn trong bối cảnh — dòng "Area" bị bỏ khỏi prompt.';
  if (w === 'NO_PLATE') return 'Có setup thiếu ảnh bối cảnh (plate) nên chưa dựng được prompt/ảnh tham chiếu — gen ảnh bối cảnh trước.';
  return w;
}

export const isLinksChanged = (err: any): boolean => err?.response?.data?.code === 'LINKS_CHANGED';

// ───────────────────────── Zone ─────────────────────────

const ZONE_ID_RE = /^[a-z0-9_-]{1,64}$/;

/** Gợi ý `id` từ tên: bỏ dấu, thường, ký tự lạ → `_`. */
export function suggestZoneId(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

/**
 * Kiểm zone TRƯỚC khi gửi `PUT /locations/:id`. BE `normalizeZones` lặng lẽ BỎ zone sai id/thiếu tên/trùng id (không báo lỗi) ⇒ người sửa tưởng đã lưu.
 * Hàng trống hoàn toàn được bỏ qua (không phải lỗi).
 */
export function validateZones(zones: FilmLocationZone[]): { clean: FilmLocationZone[]; errors: string[] } {
  const errors: string[] = [];
  const clean: FilmLocationZone[] = [];
  const seen = new Set<string>();
  zones.forEach((z, i) => {
    const id = (z.id || '').trim();
    const name = (z.name || '').trim();
    const zonePosition = (z.zonePosition || '').trim();
    if (!id && !name && !zonePosition) return;
    const at = `Zone #${i + 1}`;
    if (!ZONE_ID_RE.test(id)) errors.push(`${at}: id phải gồm chữ thường a-z, số, "_" hoặc "-" (1–64 ký tự).`);
    else if (seen.has(id)) errors.push(`${at}: id "${id}" bị trùng.`);
    if (!name) errors.push(`${at}: thiếu tên.`);
    else if (name.length > 100) errors.push(`${at}: tên quá 100 ký tự.`);
    if (zonePosition.length > 500) errors.push(`${at}: mô tả vị trí quá 500 ký tự.`);
    seen.add(id);
    clean.push({ id, name, zonePosition });
  });
  return { clean, errors };
}

/** 261010 P2b-1 — `storyId` của panel sơ đồ: prop (khung mới, không có FilmPlanProvider) thắng context (UI cũ). Không có cả hai ⇒ ''. */
export const resolveStoryId = (propStoryId: string | undefined | null, ctxStoryId: string | undefined | null): string => propStoryId || ctxStoryId || '';
